import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { useState } from 'react';

import { AudioFileSVG, DocFileSVG, VideoFileSVG } from '@components/icon/svg';
import { getFileTypeDisplay } from './getFileTypeDisplay';

/**
 * Configure-File modal for Text / Audio / Video uploads.
 *
 * PII-redaction controls used to live here, but were pulled up to the
 * tab level (FileUpload.jsx) so the option is discoverable BEFORE
 * files are dropped — matching the Document tab pattern and fixing
 * DevOps bug #2184 ("Redact PII missing from Text/Audio/Video").
 * The shared component lives in PiiRedactionControls.jsx.
 *
 * This dialog is now just: file list + language choice. onAdd fires
 * with { language, selectedOptions } — the tab merges its own
 * redaction state in before building the backend payload, so the wire
 * shape is unchanged.
 */
export const ConfigureDialog = ({ open, files, onClose, onAdd, onRemoveFile }) => {
  const [language, setLanguage] = useState('en');
  const [selectedOptions, setSelectedOptions] = useState([]);

  const handleAdd = () => {
    if (files && files.length > 0) {
      onAdd({ language, selectedOptions });
      setSelectedOptions([]);
      onClose();
    }
  };

  const handleRemoveFile = (file) => {
    if (onRemoveFile) {
      onRemoveFile(file);
    }
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      sx={{
        '& .MuiDialog-paper': {
          borderRadius: '12px',
          p: 1,
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 2,
          py: 1.5,
        }}
      >
        <Typography variant="h6">Configure File</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose} variant="text" sx={{ color: 'text.secondary' }}>
            Cancel
          </Button>
          <Button
            onClick={handleAdd}
            variant="contained"
            sx={{
              textTransform: 'none',
            }}
          >
            Add
          </Button>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 2 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)' },
              gap: 2,
            }}
          >
            {files?.map((file) => (
              <Box
                key={file.name}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,

                  border: '1px solid #C9B7FF',
                  bgcolor: '#f8f7ff',
                  borderRadius: 1.5,
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    p: 1,
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  {file.type.split('/')[0] === 'audio' ? (
                    <AudioFileSVG />
                  ) : file.type.split('/')[0] === 'video' ? (
                    <VideoFileSVG />
                  ) : (
                    <DocFileSVG />
                  )}
                </Box>
                <Box sx={{ flex: 1 }}>
                  <Typography
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      maxWidth: '150px',
                      fontWeight: 500,
                    }}
                    variant="body1"
                  >
                    {file.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {getFileTypeDisplay(file.type)} • {(file.size / 1024).toFixed(0)} KB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-85%)' }}
                  onClick={() => handleRemoveFile(file)}
                >
                  <CloseIcon size={16} />
                </IconButton>
              </Box>
            ))}
          </Box>

          {/* Language Selection */}
          <Box>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Select Language
            </Typography>
            <Select
              value={language}
              fullWidth
              size="small"
              onChange={(e) => setLanguage(e.target.value)}
              sx={{
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                },
              }}
            >
              {[
                { code: 'en', name: 'English (US)' },
                { code: 'es', name: 'Spanish' },
                { code: 'fr', name: 'French' },
                { code: 'de', name: 'German' },
                { code: 'it', name: 'Italian' },
                { code: 'pt', name: 'Portuguese' },
                { code: 'nl', name: 'Dutch' },
                { code: 'zh', name: 'Chinese' },
                { code: 'fi', name: 'Finnish' },
                { code: 'hi', name: 'Hindi' },
                { code: 'ko', name: 'Korean' },
                { code: 'pl', name: 'Polish' },
                { code: 'ru', name: 'Russian' },
                { code: 'tr', name: 'Turkish' },
                { code: 'uk', name: 'Ukrainian' },
                { code: 'ja', name: 'Japanese' },
                { code: 'vi', name: 'Vietnamese' },
              ].map((lang) => (
                <MenuItem key={lang.code} value={lang.code}>
                  <Box sx={{ mr: 1 }} />
                  {lang.name}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {/* PII-redaction controls intentionally NOT rendered here — they
              live at the tab level in FileUpload.jsx (via
              PiiRedactionControls) so users can configure them BEFORE
              staging any files. See DevOps #2184. */}
        </Stack>
      </DialogContent>
    </Dialog>
  );
};
